import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Building2, TrendingUp, Shield, Award, Users } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

const CorporateUpdatesPage = () => {
  React.useEffect(() => {
    document.title = "Corporate Updates | ItzGreen Music";
  }, []);

  const updates = [
    {
      id: 1,
      date: "2024-03-20",
      category: "Infrastructure",
      title: "Enhanced Liability Shield Protocol Implementation",
      summary: "ItzGreen Music has completed the rollout of our enhanced Liability Shield Protocol, ensuring 100% indemnification across all catalog assets.",
      details: [
        "Upgraded BMI reconciliation systems with real-time verification",
        "Implemented automated MLC registration for all new submissions",
        "Enhanced one-stop clearance documentation process",
        "Zero-friction access for verified network partners"
      ],
      impact: "Critical",
      icon: Shield
    },
    {
      id: 2,
      date: "2024-03-15",
      category: "Business Development",
      title: "Strategic Partnership Expansion with Major Networks",
      summary: "New exclusive licensing agreements secured with three major streaming platforms and two theatrical trailer production houses.",
      details: [
        "Netflix Original Series sync licensing partnership established",
        "HBO Max trailer campaign exclusive supplier status",
        "Apple Commercial division preferred vendor agreement",
        "Ford/WPP automotive campaign music supplier contract"
      ],
      impact: "High",
      icon: Building2
    },
    {
      id: 3,
      date: "2024-03-10",
      category: "Catalog Growth",
      title: "Q1 2024 Catalog Expansion: 40% Growth in Premium Assets",
      summary: "ItzGreen Music catalog has expanded by 40% in Q1 2024, with focus on Commercial Lifestyle and Authentic Cinematic Urban genres.",
      details: [
        "127 new tracks added to Supervisor Vault",
        "15 new BMI-affiliated composers onboarded",
        "100% human-produced guarantee maintained across all assets",
        "Zero AI-generated content policy strictly enforced"
      ],
      impact: "Medium",
      icon: TrendingUp
    },
    {
      id: 4,
      date: "2024-03-05",
      category: "Technology",
      title: "Artist Intake Portal Launch",
      summary: "New secure artist submission system launched for vetted composers and producers.",
      details: [
        "Streamlined submission process with automated compliance verification",
        "24-bit WAV master and stem folder upload capabilities",
        "Real-time PRO reconciliation status tracking",
        "Enhanced metadata audit and quality control systems"
      ],
      impact: "Medium",
      icon: Users
    },
    {
      id: 5,
      date: "2024-02-28",
      category: "Recognition",
      title: "Industry Recognition: Sync Licensing Excellence Award",
      summary: "ItzGreen Music recognized by Music Supervisor Guild for outstanding B2B sync administration and clearance efficiency.",
      details: [
        "Fastest clearance turnaround time in industry (sub-24 hours)",
        "Zero legal disputes or clearance issues in 2023",
        "100% client satisfaction rating from network partners",
        "Preferred vendor status with 12 major production companies"
      ],
      impact: "High",
      icon: Award
    }
  ];

  const getImpactColor = (impact) => {
    switch (impact) {
      case "Critical":
        return "text-red-400 bg-red-400/10 border-red-400/30";
      case "High":
        return "text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.1)] border-[rgba(218,255,1,0.3)]";
      case "Medium":
        return "text-blue-400 bg-blue-400/10 border-blue-400/30";
      default:
        return "text-[rgb(161,161,170)] bg-[rgba(161,161,170,0.1)] border-[rgba(161,161,170,0.3)]";
    }
  };

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO ABOUT
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Building2 size={12} className="mr-2" />
            CORPORATE COMMUNICATIONS
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            Corporate Updates
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-lg leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Strategic developments, infrastructure enhancements, and business milestones from ItzGreen Music leadership.
          </p>
        </motion.div>

        {/* Updates Timeline */}
        <div className="space-y-8">
          {updates.map((update, index) => (
            <motion.div
              key={update.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 sm:p-8"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[rgba(218,255,1,0.1)] border border-[rgba(218,255,1,0.3)] rounded-xl p-3">
                    <update.icon size={24} className="text-[rgb(218,255,1)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="text-[rgb(161,161,170)] text-xs tracking-[0.1em]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {update.category.toUpperCase()}
                      </span>
                      <div className="w-1 h-1 bg-[rgb(161,161,170)] rounded-full"></div>
                      <div className="flex items-center gap-2 text-[rgb(161,161,170)] text-xs">
                        <Calendar size={12} />
                        {new Date(update.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                    <h2
                      className="text-white text-xl sm:text-2xl font-bold mb-3"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    >
                      {update.title}
                    </h2>
                  </div>
                </div>
                
                <Badge
                  className={`${getImpactColor(update.impact)} text-xs font-medium px-3 py-1 rounded-lg`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {update.impact.toUpperCase()} IMPACT
                </Badge>
              </div>

              <p
                className="text-[rgb(200,200,210)] text-base leading-relaxed mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {update.summary}
              </p>

              <div className="space-y-3">
                <h3
                  className="text-white text-sm font-bold tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  KEY DEVELOPMENTS:
                </h3>
                <ul className="space-y-2">
                  {update.details.map((detail, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-[rgb(161,161,170)] text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      <div className="w-1.5 h-1.5 bg-[rgb(218,255,1)] rounded-full mt-2 flex-shrink-0"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <Separator className="bg-[rgb(63,63,63)] my-12" />

        {/* Contact for Updates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-[rgba(218,255,1,0.05)] border border-[rgba(218,255,1,0.2)] rounded-xl p-6 text-center"
        >
          <h3
            className="text-white text-lg font-bold mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            CORPORATE COMMUNICATIONS
          </h3>
          <p
            className="text-[rgb(161,161,170)] text-sm mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            For press inquiries, partnership announcements, or corporate communications.
          </p>
          <a
            href="mailto:leaver@itzgreenmusic.com"
            className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-bold tracking-wider px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] transition-all duration-300"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            CONTACT LEADERSHIP
          </a>
        </motion.div>
      </div>
    </main>
  );
};

export default CorporateUpdatesPage;